/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchIntros,
  updateIntro,
  deleteIntro as _deleteIntro,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Intro, LoadStatus } from "../../../types";

export interface IntrosState {
  intros: Record<string, Intro>;
  introEdits: Record<string, Edits<Intro>>;
  introLoadStatus: LoadStatus;
}

const initialState: IntrosState = {
  intros: {},
  introEdits: {},
  introLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadIntros = createAsyncThunk(
  "intros/loadIntros",
  async (_, thunkAPI): Promise<Intro[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchIntros();
  }
);

export const addOrUpdateIntro = createAsyncThunk(
  "intros/addOrUpdateIntro",
  async (data: Partial<Intro>, thunkAPI): Promise<Intro> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateIntro(state.login.accessToken, data);
  }
);

export const deleteIntro = createAsyncThunk(
  "intros/deleteIntro",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteIntro(state.login.accessToken, id);
  }
);

export const revertIntro = createAsyncThunk(
  "intros/revertIntro",
  async (id: string, thunkAPI): Promise<Intro> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.intros.introEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateIntro(state.login.accessToken, edits.oldData);
  }
);

export const introsSlice = createSlice({
  name: "intros",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load intros
      .addCase(loadIntros.pending, (state) => {
        state.introLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadIntros.fulfilled, (state, action) => {
        for (const intro of action.payload) {
          state.intros[intro.id] = intro;
          state.introEdits[intro.id] = {
            oldData: intro,
          };
        }
        state.introLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadIntros.rejected, (state) => {
        state.introLoadStatus = LoadStatus.FAILED;
      })
      // add or update intro
      .addCase(addOrUpdateIntro.fulfilled, (state, action) => {
        const intro = action.payload;
        if (state.intros[intro.id]) {
          state.introEdits[intro.id].newData = intro;
        } else {
          state.introEdits[intro.id] = {
            oldData: intro,
            created: true,
          };
        }
        state.intros[intro.id] = intro;
      })
      // delete intro
      .addCase(deleteIntro.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.intros[id];
      })
      // revert intro
      .addCase(revertIntro.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.intros[id] = action.payload;
        state.introEdits[id].oldData = action.payload;
        state.introEdits[id].newData = undefined;
      });
  },
});

export default introsSlice.reducer;
